/**
 * Shoulder Pain at Night Page
 * SEO-optimized page targeting "shoulder pain at night", "shoulder hurts when sleeping"
 * 914 impressions on main site - high intent symptom search
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { CTASection } from '../components/CTASection';
import { FAQSection } from '../components/FAQSection';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Moon, AlertTriangle, Bed, ArrowRight, Clock, Stethoscope, Activity, CheckCircle, Phone, IndianRupee } from 'lucide-react';
import { useSite } from '../contexts/SiteContext';

export const ShoulderPainAtNight = () => {
  const { theme, doctor } = useSite();

  // Common causes of night shoulder pain with links to condition pages
  const causes = [
    {
      condition: "Rotator Cuff Tear",
      description: "The most common cause of night pain. Lying down increases pressure on the damaged tendons, causing pain that wakes you up.",
      severity: "Common",
      link: "/rotator-cuff-tear"
    },
    {
      condition: "Frozen Shoulder (Adhesive Capsulitis)",
      description: "Night pain is a hallmark of frozen shoulder, especially in the 'freezing' phase. The capsule becomes inflamed and stiff.",
      severity: "Very Common",
      link: "/frozen-shoulder"
    },
    {
      condition: "Shoulder Impingement",
      description: "Lying on the affected side compresses the rotator cuff against the acromion, causing sharp pain.",
      severity: "Common",
      link: "/shoulder-impingement"
    },
    {
      condition: "Calcific Tendonitis",
      description: "During the resorptive phase, calcium deposits cause intense inflammation that worsens at night.",
      severity: "Moderate",
      link: "/calcific-tendonitis"
    },
    {
      condition: "Shoulder Arthritis",
      description: "Joint inflammation increases when you're still for long periods. Morning stiffness is also common.",
      severity: "Moderate",
      link: "/shoulder-arthritis"
    },
    {
      condition: "Labral Tears",
      description: "Deep, aching pain that can worsen at night, especially with certain sleeping positions.",
      severity: "Less Common",
      link: "/labral-tears"
    }
  ];

  // Sleep position tips
  const sleepTips = [
    {
      title: "Sleep on Your Back",
      description: "Use a pillow under the affected arm to support it slightly elevated. This reduces pressure on the rotator cuff."
    },
    {
      title: "Avoid the Affected Side",
      description: "Lying directly on the painful shoulder compresses the already irritated structures. Sleep on the opposite side or back."
    },
    {
      title: "Hug a Pillow",
      description: "If sleeping on your side (non-affected), hug a pillow to prevent the top arm from falling forward and straining the shoulder."
    },
    {
      title: "Reclined Position",
      description: "Some patients find relief sleeping in a reclined position (like in a recliner) at 45 degrees during acute flares."
    },
    {
      title: "Supportive Mattress",
      description: "A medium-firm mattress provides better shoulder support than very soft mattresses."
    }
  ];

  // Red flags - when to seek urgent care
  const redFlags = [
    "Severe pain after a fall or injury",
    "Inability to move the arm at all",
    "Visible deformity of the shoulder",
    "Pain accompanied by chest pain or shortness of breath",
    "Fever with shoulder pain",
    "Rapid swelling or redness"
  ];

  // FAQs for schema
  const faqs = [
    { 
      question: "Why does my shoulder hurt more at night?",
      answer: "Night shoulder pain occurs for several reasons: 1) Lying down changes blood flow and increases pressure in the shoulder joint, 2) The rotator cuff gets compressed when you lie on it, 3) Inflammatory chemicals (cytokines) peak at night, 4) You're more aware of pain without daytime distractions, 5) Stillness allows inflammation to build up. Conditions like rotator cuff tears and frozen shoulder are notorious for night pain."
    },
    {
      question: "What sleeping position is best for shoulder pain?",
      answer: "The best position is sleeping on your back with a small pillow under the affected arm for support. If you must sleep on your side, sleep on the unaffected side and hug a pillow to support the painful arm. Avoid sleeping directly on the painful shoulder. Some people find relief sleeping slightly reclined (45 degrees) during acute flares."
    },
    {
      question: "When should I see a doctor for shoulder pain at night?",
      answer: "See a shoulder specialist if: pain persists for more than 2 weeks, pain regularly wakes you from sleep, you have weakness lifting your arm, pain follows an injury, over-the-counter medications don't help, or you notice any red flags like fever, deformity, or inability to move the arm. Early treatment prevents conditions from worsening."
    },
    {
      question: "Can shoulder pain at night be cured?",
      answer: "Yes, in most cases. The treatment depends on the underlying cause. Rotator cuff tears may need physiotherapy or surgery. Frozen shoulder typically resolves in 1-2 years but can be accelerated with treatment. Impingement often responds to injections and exercises. Dr. Deepthi Nandan Reddy will diagnose the cause and recommend appropriate treatment at Apollo Hospitals Hyderabad."
    },
    {
      question: "What home remedies help shoulder pain at night?",
      answer: "Home remedies include: 1) Ice the shoulder for 15-20 minutes before bed, 2) Take anti-inflammatory medication (ibuprofen) 30 minutes before sleep, 3) Use proper pillow positioning, 4) Gentle stretching before bed, 5) Avoid overhead activities during the day, 6) Apply heat if the pain is from stiffness rather than inflammation. However, persistent night pain needs professional evaluation."
    },
    {
      question: "How much does it cost to treat shoulder pain in Hyderabad?",
      answer: "At Apollo Hospitals Financial District: Consultation ₹800-1,500, X-ray + Ultrasound ₹1,500-3,000, MRI ₹8,000-12,000, Corticosteroid injection ₹2,000-4,000, Physiotherapy (per session) ₹500-1,000. Surgical costs vary: Arthroscopic rotator cuff repair ₹1.5-2.5 lakhs, Shoulder replacement ₹3-5 lakhs. Most insurance policies cover surgical treatment."
    }
  ];

  return (
    <div className="min-h-screen" data-testid="shoulder-pain-at-night-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Moon className="w-10 h-10 text-yellow-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Shoulder Pain at Night – Causes & Treatment in Hyderabad
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Waking up with shoulder pain? Can't sleep on your side? Expert diagnosis and treatment 
              by Dr. Deepthi Nandan Reddy at Apollo Hospitals, Hyderabad.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={doctor.bookingLink} target="_blank" rel="noopener noreferrer">
                <button className="px-8 py-4 rounded-lg font-medium flex items-center justify-center gap-2 bg-white hover:bg-gray-100 transition-colors" style={{ color: theme?.primaryHex }}>
                  <Phone className="w-5 h-5" />
                  Book Consultation
                </button>
              </a>
              <a href={`tel:${doctor.phone}`}>
                <button className="px-8 py-4 rounded-lg border-2 border-white text-white font-medium flex items-center justify-center gap-2 hover:bg-white/10 transition-colors">
                  Call {doctor.phone}
                </button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why Does Shoulder Hurt at Night */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Does Your Shoulder Hurt More at Night?</h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                Night shoulder pain is one of the most common complaints in orthopedic practice. Patients often describe being 
                woken from sleep by sharp or aching pain, or being unable to find a comfortable sleeping position. This isn't 
                just coincidence – there are specific physiological reasons why shoulder pain worsens at night:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg my-6">
                <ul className="space-y-3">
                  <li><strong>Increased joint pressure:</strong> When you lie down, blood flow changes and fluid accumulates in the shoulder joint, increasing pressure on inflamed structures.</li>
                  <li><strong>Compression of rotator cuff:</strong> Lying on your side directly compresses the already irritated rotator cuff tendons against the bone.</li>
                  <li><strong>Inflammatory cycle:</strong> The body's inflammatory chemicals (cytokines) naturally peak during nighttime hours.</li>
                  <li><strong>Reduced distractions:</strong> Without daytime activities to distract you, pain becomes more noticeable.</li>
                  <li><strong>Stillness effect:</strong> Prolonged immobility allows inflammation to build up around the joint.</li>
                </ul>
              </div>
              <p>
                If your shoulder pain at night persists for more than 2 weeks or regularly wakes you from sleep, it's important 
                to see a shoulder specialist. Night pain is often a sign of a condition that needs treatment – ignoring it 
                typically leads to worsening symptoms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Common Causes */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Activity className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Common Causes of Shoulder Pain at Night</h2>
            </div>
            <p className="text-gray-600 mb-8">Click on any condition to learn more about symptoms, diagnosis, and treatment options in Hyderabad.</p>
            
            <div className="grid md:grid-cols-2 gap-6">
              {causes.map((cause, index) => (
                <Link key={index} to={cause.link}>
                  <Card className="h-full hover:shadow-lg transition-all cursor-pointer border-l-4" style={{ borderLeftColor: theme?.primaryHex }}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-lg">{cause.condition}</CardTitle>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          cause.severity === 'Very Common' ? 'bg-red-100 text-red-700' :
                          cause.severity === 'Common' ? 'bg-orange-100 text-orange-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {cause.severity}
                        </span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 text-sm mb-3">{cause.description}</p>
                      <span className="text-sm font-medium flex items-center gap-1" style={{ color: theme?.primaryHex }}>
                        Learn about treatment <ArrowRight className="w-4 h-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Sleep Position Tips */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <Bed className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Best Sleeping Positions for Shoulder Pain</h2>
            </div>
            
            <div className="space-y-4">
              {sleepTips.map((tip, index) => (
                <div key={index} className="flex gap-4 p-4 bg-gray-50 rounded-lg">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold" style={{ backgroundColor: theme?.primaryHex }}>
                    {index + 1}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{tip.title}</h3>
                    <p className="text-gray-600 text-sm">{tip.description}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 rounded-lg" style={{ backgroundColor: `${theme?.primaryHex}10` }}>
              <h3 className="font-semibold text-gray-900 mb-3">Quick Relief Tips Before Bed</h3>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: theme?.primaryHex }} />
                  <span>Apply ice for 15-20 minutes before sleeping</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: theme?.primaryHex }} />
                  <span>Take ibuprofen 30 minutes before bed</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: theme?.primaryHex }} />
                  <span>Gentle pendulum stretches before lying down</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: theme?.primaryHex }} />
                  <span>Avoid heavy lifting or overhead work during the day</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When to See a Doctor */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Stethoscope className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">When to See a Shoulder Specialist</h2>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5" style={{ color: theme?.primaryHex }} />
                    See a Doctor Soon (Within 1-2 Weeks)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-gray-700">
                  <p>• Pain persists for more than 2 weeks</p>
                  <p>• Pain regularly wakes you from sleep</p>
                  <p>• Weakness when lifting your arm</p>
                  <p>• Pain limits daily activities</p>
                  <p>• Over-the-counter medications don't help</p>
                  <p>• Stiffness that's getting worse</p>
                </CardContent>
              </Card>

              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-700">
                    <AlertTriangle className="w-5 h-5" />
                    Seek Urgent Care (Same Day)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-gray-700">
                  {redFlags.map((flag, index) => (
                    <p key={index}>• {flag}</p>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Cost Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <IndianRupee className="w-8 h-8" style={{ color: theme?.primaryHex }} />
              <h2 className="text-3xl font-bold text-gray-900">Cost of Treatment in Hyderabad</h2>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg">
              <p className="text-gray-600 mb-4">Indicative costs at Apollo Hospitals Financial District:</p>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Consultation with Dr. Reddy</span>
                  <span className="font-semibold">₹800-1,500</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">X-ray + Ultrasound</span>
                  <span className="font-semibold">₹1,500-3,000</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">MRI Shoulder</span>
                  <span className="font-semibold">₹8,000-12,000</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Corticosteroid Injection</span>
                  <span className="font-semibold">₹2,000-4,000</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                  <span className="text-gray-700">Physiotherapy (per session)</span>
                  <span className="font-semibold">₹500-1,000</span>
                </div>
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-700">Arthroscopic Rotator Cuff Repair</span>
                  <span className="font-semibold">₹1.5-2.5 Lakhs</span>
                </div>
              </div>
              <p className="text-sm text-gray-500 mt-4">Most insurance policies cover surgical treatment. Contact Apollo Hospitals for exact pricing.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Doctor Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-col md:flex-row gap-8 items-center bg-white p-8 rounded-xl shadow-sm">
              <img 
                src={doctor.doctorImage} 
                alt={doctor.name}
                className="w-32 h-32 rounded-full object-cover border-4"
                style={{ borderColor: theme?.primaryHex }}
              />
              <div className="text-center md:text-left">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Get Expert Diagnosis</h2>
                <p className="text-gray-600 mb-4">
                  Night shoulder pain needs accurate diagnosis to treat effectively. Dr. Deepthi Nandan Reddy has 30+ years 
                  of experience treating shoulder conditions. UK & Mayo Clinic fellowship trained. At Apollo Hospitals 
                  Financial District, Hyderabad.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  <a href={doctor.bookingLink} target="_blank" rel="noopener noreferrer">
                    <button 
                      className="px-6 py-3 rounded-lg text-white font-medium hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: theme?.primaryHex }}
                    >
                      Book Appointment at Apollo
                    </button>
                  </a>
                  <a href={`tel:${doctor.phone}`}>
                    <button 
                      className="px-6 py-3 rounded-lg border-2 font-medium hover:bg-gray-50 transition-colors"
                      style={{ borderColor: theme?.primaryHex, color: theme?.primaryHex }}
                    >
                      Call {doctor.phone}
                    </button>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Conditions */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Related Conditions & Resources</h3>
            <div className="flex flex-wrap gap-3">
              <Link to="/rotator-cuff-tear" className="px-4 py-2 rounded-full text-sm hover:opacity-80 transition-opacity" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>Rotator Cuff Tear</Link>
              <Link to="/frozen-shoulder" className="px-4 py-2 rounded-full text-sm hover:opacity-80 transition-opacity" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>Frozen Shoulder</Link>
              <Link to="/shoulder-impingement" className="px-4 py-2 rounded-full text-sm hover:opacity-80 transition-opacity" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>Shoulder Impingement</Link>
              <Link to="/calcific-tendonitis" className="px-4 py-2 rounded-full text-sm hover:opacity-80 transition-opacity" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>Calcific Tendonitis</Link>
              <Link to="/shoulder-arthritis" className="px-4 py-2 rounded-full text-sm hover:opacity-80 transition-opacity" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>Shoulder Arthritis</Link>
              <Link to="/shoulder-pain-treatment-hyderabad" className="px-4 py-2 rounded-full text-sm hover:opacity-80 transition-opacity" style={{ backgroundColor: `${theme?.primaryHex}15`, color: theme?.primaryHex }}>All Shoulder Conditions</Link>
              <a href="https://drdnreddyortho.com/about" className="px-4 py-2 rounded-full text-sm hover:opacity-80 transition-opacity border" style={{ borderColor: theme?.primaryHex, color: theme?.primaryHex }}>About Dr. Reddy →</a>
            </div>
          </div>
        </div>
      </section>

      <FAQSection faqs={faqs} />
      <CTASection />
    </div>
  );
};

export default ShoulderPainAtNight;
